import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { EMPTY_PET } from './customerPets.constants.js';

function breedParent(b) {
  return String(b.parentId?._id || b.parentId || '');
}

/**
 * Inline editor for the animals to create together with a new customer. Fully
 * controlled: holds no internal state, the parent owns the `value` array. Mirrors
 * the species/breed selects of PetFormModal (breeds filtered by species).
 */
export function CustomerPetsEditor({ value, onChange, speciesOpts = [], breedOpts = [], disabled = false }) {
  const pets = Array.isArray(value) ? value : [];

  const breedsBySpecies = useMemo(() => {
    const map = new Map();
    for (const b of breedOpts) {
      const key = breedParent(b);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    return map;
  }, [breedOpts]);

  const updateAt = (i, patch) => {
    const next = pets.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    onChange(next);
  };
  const removeAt = (i) => onChange(pets.filter((_, idx) => idx !== i));
  const add = () => onChange([...pets, { ...EMPTY_PET }]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-ink">Animali{pets.length ? ` (${pets.length})` : ''}</p>
        <Button type="button" size="sm" variant="ghost" onClick={add} disabled={disabled}>
          <Plus className="h-4 w-4" />Aggiungi animale
        </Button>
      </div>

      {pets.length === 0 ? (
        <p className="text-xs text-ink-faint">Nessun animale. Aggiungilo manualmente o estrai dalla bozza.</p>
      ) : (
        pets.map((pet, i) => {
          const filteredBreeds = breedsBySpecies.get(String(pet.speciesId || '')) || [];
          return (
            <div key={i} className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-ink-muted">Animale {i + 1}</span>
                <button
                  type="button"
                  className="text-ink-faint hover:text-danger disabled:opacity-50"
                  title="Rimuovi animale"
                  disabled={disabled}
                  onClick={() => removeAt(i)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input label="Nome" value={pet.name} disabled={disabled} onChange={(e) => updateAt(i, { name: e.target.value })} />
                <Select label="Sesso" value={pet.sex || 'unknown'} disabled={disabled} onChange={(e) => updateAt(i, { sex: e.target.value })}>
                  <option value="unknown">Sconosciuto</option>
                  <option value="male">Maschio</option>
                  <option value="female">Femmina</option>
                </Select>
                <Select
                  label="Specie"
                  value={pet.speciesId || ''}
                  disabled={disabled}
                  onChange={(e) => updateAt(i, { speciesId: e.target.value, breedId: '' })}
                >
                  <option value="">-</option>
                  {speciesOpts.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Select>
                <Select
                  label="Razza"
                  value={pet.breedId || ''}
                  disabled={disabled || !pet.speciesId}
                  onChange={(e) => updateAt(i, { breedId: e.target.value })}
                >
                  <option value="">{pet.speciesId ? '-' : 'Seleziona prima la specie'}</option>
                  {filteredBreeds.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Select>
                <Input label="Data di nascita" type="date" value={pet.birthDate || ''} disabled={disabled} onChange={(e) => updateAt(i, { birthDate: e.target.value })} />
                <Input label="Peso (kg)" type="number" step="0.1" min={0} value={pet.weightKg ?? ''} disabled={disabled} onChange={(e) => updateAt(i, { weightKg: e.target.value })} />
              </div>

              <TagInput
                label="Allergie"
                value={pet.allergies || []}
                onChange={(v) => updateAt(i, { allergies: v })}
                placeholder="Aggiungi e premi Invio"
                lowercase
              />
              <Textarea label="Note" rows={2} value={pet.dietaryNotes || ''} disabled={disabled} onChange={(e) => updateAt(i, { dietaryNotes: e.target.value })} />
            </div>
          );
        })
      )}
    </section>
  );
}
